// Structural IBAN validation: the ISO 13616 shape plus the mod-97 checksum. This is
// the check that holds when the bank-data provider cannot answer — a lookup that
// returns UNAVAILABLE (no API key, outage, quota) must not let `iban: 'x'` through.
// It says nothing about whether the account exists; that is the provider's job.
//
// Deliberately country-agnostic. Per-country lengths would catch a little more, but a
// wrong or missing entry silently refuses every IBAN from that country — too much
// downside for a check whose job is to stop '-', 'n/a' and 'x'. Both parts here are
// universal: the shape holds for every IBAN ever issued, and mod-97 is arithmetic on
// the value itself, so neither can block a country.

// ISO 13616: 2-letter country, 2 check digits, then up to 30 alphanumerics.
const IBAN_SHAPE = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/;

// IBANs are printed in groups of four, so a pasted one routinely carries spaces.
export const normalizeIban = (iban: string): string => iban.replace(/[\s-]/g, '').toUpperCase();

// mod-97 over the rearranged string, computed in chunks so the value never exceeds
// the safe integer range (a full IBAN is far wider than Number can hold).
const mod97 = (rearranged: string): number => {
	let remainder = 0;
	for (const char of rearranged) {
		const value = char >= 'A' && char <= 'Z' ? `${char.charCodeAt(0) - 55}` : char;
		for (const digit of value) {
			remainder = (remainder * 10 + Number(digit)) % 97;
		}
	}
	return remainder;
};

export const isStructurallyValidIban = (iban: string | null | undefined): boolean => {
	if (!iban) {
		return false;
	}
	const normalized = normalizeIban(iban);
	if (!IBAN_SHAPE.test(normalized)) {
		return false;
	}
	// The first four characters move to the end before the checksum is taken.
	return mod97(normalized.slice(4) + normalized.slice(0, 4)) === 1;
};
